import { 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogContentText, 
    DialogTitle,
    Box,
    Typography,
    Stack,
    Alert,
    TextField
} from "@mui/material";
import { 
    Warning, 
    People, 
    MenuBook, 
    AttachMoney,
    DeleteForever 
} from "@mui/icons-material";
import { useState } from "react";

interface DeleteClassroomDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    deleteFunction: (password: string) => void;
}

export default function DeleteClassroomDialog({ open, setOpen, deleteFunction }: DeleteClassroomDialogProps) {
   
    const [password, setPassword] = useState("");

    const handleClose = () => {
        setPassword("");
        setOpen(false)
    };


    const handleProceed = () => {
        setOpen(false);
        deleteFunction(password);
        setPassword(password);
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="delete-classroom-dialog-title"
            aria-describedby="delete-classroom-dialog-description"
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    overflow: 'visible'
                }
            }}
        >
            <Box sx={{ position: 'relative', textAlign: 'center', pt: 3, pb: 1 }}>
                <DialogTitle 
                    id="delete-classroom-dialog-title"
                    sx={{ 
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: '#dc2626',
                        textAlign: 'center',
                        pb: 1,
                        pt: 0
                    }}
                >
                    Delete Classroom Permanently?
                </DialogTitle>
            </Box>

            <DialogContent sx={{ pt: 1, pb: 2 }}>
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                            fontSize: '0.95rem'
                        }
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        ⚠️ This action is permanent and irreversible
                    </Typography>
                    <Typography variant="body2">
                        Once deleted, this classroom and all associated data cannot be recovered.
                    </Typography>
                </Alert>

                <DialogContentText 
                    id="delete-classroom-dialog-description"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        textAlign: 'center',
                        mb: 3
                    }}
                >
                    Deleting this classroom will result in the following consequences:
                </DialogContentText>

                <Stack spacing={2}>
                    <Box sx={{ 
                        backgroundColor: '#fef2f2', 
                        border: '1px solid #fecaca',
                        borderRadius: 2, 
                        p: 2 
                    }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <People sx={{ fontSize: 18, color: '#dc2626' }} />
                                </Box>
                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                    All enrolled students will immediately lose access to the classroom
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <MenuBook sx={{ fontSize: 18, color: '#dc2626' }} />
                                </Box>
                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                    All lectures, assessments, library collections, and materials will be permanently deleted
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    width: 36, 
                                    height: 36, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <AttachMoney sx={{ fontSize: 18, color: '#dc2626' }} />
                                </Box>
                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                        You will lose access to all earnings history and financial data associated with this classroom
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>

                <TextField
                    type="password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password to confirm"
                    size="small"
                    variant="outlined"
                    sx={{
                        '& input': { fontSize: '0.9rem' },
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        marginTop: 3
                    }}
                />
                
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2, justifyContent: 'center' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 4,
                        py: 1.2,
                        borderColor: '#d1d5db',
                        color: '#6b7280',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb'
                        }
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleProceed}
                    variant="contained"
                    disabled={!password.trim()}
                    sx={{ 
                        backgroundColor: "#dc2626",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.2,
                        boxShadow: '0 4px 12px -2px rgba(220, 38, 38, 0.3)',
                        '&:hover': {
                            backgroundColor: "#b91c1c",
                            boxShadow: '0 8px 20px -4px rgba(220, 38, 38, 0.4)',
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );
}